import { useQuery, useMutation } from '@tanstack/react-query';

export type ModoConflitoSGTP = 'SGTP_SEMPRE' | 'MANUAL_TEMPORARIO' | 'DECISAO_ASSISTIDA';

export interface PoliticaPrevalencia {
  id: string;
  modo: ModoConflitoSGTP;
  observacao: string | null;
  alterado_por: string | null;
  alterado_em: string;
  updated_at: string;
}

export const MODO_LABELS: Record<ModoConflitoSGTP, string> = {
  SGTP_SEMPRE: 'SGTP sempre prevalece',
  MANUAL_TEMPORARIO: 'Manual temporário',
  DECISAO_ASSISTIDA: 'Decisão assistida',
};

export const MODO_DESCRICOES: Record<ModoConflitoSGTP, string> = {
  SGTP_SEMPRE:
    'Em qualquer divergência, o dado vindo do SGTP prevalece automaticamente. Edições manuais ficam apenas como registro auxiliar.',
  MANUAL_TEMPORARIO:
    'O dado editado manualmente prevalece até a próxima sincronização do SGTP. Útil para contingências pontuais.',
  DECISAO_ASSISTIDA:
    'Conflitos são detectados e exibidos como alerta; um analista decide caso a caso. Recomendado por padrão.',
};

/**
 * Mensagem mostrada no lugar de uma gravação real (CLAUDE.md, "Regras de
 * interface", item 8: mensagem ao usuário diz o que fazer, nunca mostra erro
 * técnico). Usada por `useUpdatePoliticaPrevalencia`, abaixo.
 */
const AVISO_DE_ACAO_INDISPONIVEL =
  'Estamos preparando esta funcionalidade. Em breve você poderá salvar por aqui.';

/**
 * TEMPORÁRIO — mock local, remover no desenvolvimento do backend. Não existe
 * módulo de governança/SGTP em `apps/api/src/modulos/` (confirmado: nenhuma
 * rota de política de prevalência ou de auditoria de decisões SGTP) —
 * decisão do dono de 2026-08-23, registrada em CLAUDE.md, seção "Proibido".
 * Some inteiro quando `GET /governanca/politica-prevalencia-sgtp` existir, e
 * `usePoliticaPrevalencia`/`useAuditoriaDecisoesSGTP` voltam a chamar
 * `chamarApi`. `useUpdatePoliticaPrevalencia` já nasce sem gravar de verdade:
 * sem rota de escrita, ela só mostra o aviso acima.
 */
const POLITICA_PREVALENCIA_MOCK: PoliticaPrevalencia = {
  id: 'politica-prevalencia-sgtp-01',
  modo: 'DECISAO_ASSISTIDA',
  observacao:
    'Modo recomendado pela DTP enquanto a integração com o SGTP está em fase de estabilização.',
  alterado_por: 'usuario-exemplo-admin-01',
  alterado_em: new Date(Date.now() - 12 * 86_400_000).toISOString(),
  updated_at: new Date(Date.now() - 12 * 86_400_000).toISOString(),
};

export function usePoliticaPrevalencia() {
  return useQuery({
    queryKey: ['politica-prevalencia-sgtp'],
    // TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
    queryFn: async (): Promise<PoliticaPrevalencia | null> => POLITICA_PREVALENCIA_MOCK,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Não grava: não há rota de escrita para esta política (ver comentário
 * TEMPORÁRIO acima). Toda tentativa de salvar termina em erro com a mensagem
 * de aviso, para a tela mostrar em vez de fingir sucesso.
 */
export function useUpdatePoliticaPrevalencia() {
  return useMutation({
    mutationFn: async (_input: {
      id: string;
      modo: ModoConflitoSGTP;
      observacao: string | null;
    }): Promise<void> => {
      throw new Error(AVISO_DE_ACAO_INDISPONIVEL);
    },
  });
}

export interface AuditoriaDecisaoSGTP {
  id: string;
  tipo_evento: string;
  entidade: string | null;
  entidade_id: string | null;
  modo_anterior: ModoConflitoSGTP | null;
  modo_novo: ModoConflitoSGTP | null;
  motivo: string | null;
  payload: Record<string, unknown> | null;
  decidido_por: string | null;
  created_at: string;
}

function timestampNoPassado(dias: number): string {
  return new Date(Date.now() - dias * 86_400_000).toISOString();
}

// TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
const AUDITORIA_DECISOES_SGTP_MOCK: AuditoriaDecisaoSGTP[] = [
  {
    id: 'auditoria-sgtp-exemplo-01',
    tipo_evento: 'ALTERACAO_POLITICA',
    entidade: null,
    entidade_id: null,
    modo_anterior: 'SGTP_SEMPRE',
    modo_novo: 'DECISAO_ASSISTIDA',
    motivo: 'Recomendação da DTP durante estabilização da integração.',
    payload: null,
    decidido_por: 'usuario-exemplo-admin-01',
    created_at: timestampNoPassado(12),
  },
  {
    id: 'auditoria-sgtp-exemplo-02',
    tipo_evento: 'CONFLITO_DETECTADO',
    entidade: 'titulares',
    entidade_id: 'titular-exemplo-07',
    modo_anterior: null,
    modo_novo: null,
    motivo: 'Divergência de endereço entre SGTP e cadastro manual.',
    payload: { campo: 'endereco' },
    decidido_por: null,
    created_at: timestampNoPassado(9),
  },
  {
    id: 'auditoria-sgtp-exemplo-03',
    tipo_evento: 'CONFLITO_RESOLVIDO',
    entidade: 'titulares',
    entidade_id: 'titular-exemplo-07',
    modo_anterior: null,
    modo_novo: null,
    motivo: 'Analista confirmou o dado do SGTP como correto.',
    payload: { campo: 'endereco', decisao: 'SGTP' },
    decidido_por: 'usuario-exemplo-analista-02',
    created_at: timestampNoPassado(8),
  },
  {
    id: 'auditoria-sgtp-exemplo-04',
    tipo_evento: 'CONFLITO_DETECTADO',
    entidade: 'veiculos',
    entidade_id: 'veiculo-exemplo-14',
    modo_anterior: null,
    modo_novo: null,
    motivo: 'Placa divergente entre SGTP e vistoria manual.',
    payload: { campo: 'placa' },
    decidido_por: null,
    created_at: timestampNoPassado(3),
  },
  {
    id: 'auditoria-sgtp-exemplo-05',
    tipo_evento: 'CONFLITO_RESOLVIDO',
    entidade: 'veiculos',
    entidade_id: 'veiculo-exemplo-14',
    modo_anterior: null,
    modo_novo: null,
    motivo: 'Analista manteve o dado manual até nova vistoria.',
    payload: { campo: 'placa', decisao: 'MANUAL' },
    decidido_por: 'usuario-exemplo-analista-02',
    created_at: timestampNoPassado(2),
  },
];

export function useAuditoriaDecisoesSGTP(limit = 50) {
  return useQuery({
    queryKey: ['auditoria-decisoes-sgtp', limit],
    // TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
    queryFn: async (): Promise<AuditoriaDecisaoSGTP[]> =>
      AUDITORIA_DECISOES_SGTP_MOCK.slice(0, limit),
  });
}
